import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormIODefinition } from "@features/bb-formio";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { IEpisode } from "@project/models/episodes/episode";
import { Location } from "@angular/common";
import { MultiUserFormDef } from "@main/formio/multi-user-form/dto/multi-user-form.dto";
import { MultiUserFormAssociation, NewMultiUserFormAssociation } from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";
import { AppStorage } from "@bbwt/utils/app-storage";
import { FormIODefinitionService } from "@features/bb-formio/services/formio-definition.service";
import { EpisodeService } from "@project/episode/episode.service";
import { MultiUserFormService } from "@main/formio/multi-user-form/services/multi-user-form.service";
import { FormioViewerService } from "@main/formio/services/formioViewer.service";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { UserService } from "@main/users";
import { MultiUserFormAssociationsService } from "@main/formio/multi-user-form/services/multi-user-form-associations.service";
import { CustomComponentTypes } from "../data-insertion-dialog/customComponentTypes";
import { removePathParam } from "@bbwt/utils/routes";

@Component({
    selector: "bbwt-formio-module-fill-form",
    templateUrl: "./formio-module-fill-form.component.html",
    styleUrl: "./formio-module-fill-form.component.scss"
})
export class FormioModuleFillFormComponent implements OnInit {

    moduleName: string = null;

    episodeRequired: boolean = false;
    currentEpisode: IEpisode = null;
    currentMUFDef: MultiUserFormDef = null;
    currentFormDef: FormIODefinition = null;

    displaySelectEpisodeDialog: boolean = false;
    displaySelectFormDialog: boolean = false;
    displaySelectPatientDialog: boolean = false;
    displayMufCreateInstanceDialog: boolean = false;
    displayNewFormRequestDialog: boolean = false;

    restoreSelectControlState: boolean = false;
    restoreDuplicateCheakState: boolean = false;

    mufAssoc: MultiUserFormAssociation = null;
    newMufAssocData: NewMultiUserFormAssociation = null;

    currentUrl: string = null;
    returnUrl: string = null;

    constructor(
        private multiUserFormAssociationService: MultiUserFormAssociationsService,
        private multiUserFormService: MultiUserFormService,
        private formIODefinitionService: FormIODefinitionService,
        private formioViewerService: FormioViewerService,
        protected patientService: PatientService,
        private episodeService: EpisodeService,
        private activatedRoute: ActivatedRoute,
        private userService: UserService,
        private location: Location,
        private router: Router) {
    }

    ngOnInit(): void {
        this.currentUrl = this.router.url;

        this.activatedRoute.data.subscribe(data => {
            if (!!data["moduleName"]) {
                this.moduleName = data["moduleName"];
                this.displaySelectFormDialog = true;

                if (!!data["returnUrl"]) {
                    this.returnUrl = data["returnUrl"];
                    this.returnUrl = this.router.createUrlTree([this.returnUrl], {relativeTo: this.activatedRoute}).toString();
                }
            }
        })
    }

    showSelectEpisodeDialog() {
        this.displaySelectEpisodeDialog = true;
    }

    handleMenuItemClick = (routerLink: string, queryParams?: { [key: string]: any }) => {
        if (queryParams) {
            this.router.navigate([routerLink], { queryParams });
        }
    };

    onOkSelectPatientDialog(patientId: string) {
        this.patientService.currentPatientId = patientId;
        this.displaySelectPatientDialog = false;
        this.formSelected(this.currentFormDef);
    }

    async formSelected(selectedDefinition: FormIODefinition) {
        this.displaySelectFormDialog = false;
        this.currentFormDef = selectedDefinition;

        if (!this.patientService.currentPatientId) {
            this.displaySelectPatientDialog = true;
            return;
        }
        await this.prepareEpisode();
    }

    async prepareEpisode() {
        const formObject = await this.formIODefinitionService.get(this.currentFormDef.id);
        const episodeForm = formObject.episode;
        this.episodeRequired = formObject.isEpisodeLocked;

        if (episodeForm) {
            this.currentEpisode = await this.formIODefinitionService.ensureInstanceEpisodeExistsForPatient(
                episodeForm,
                this.patientService.currentPatientId
            );
        }
        this.restoreDuplicateCheakState = this.restoreSelectControlState = false;

        if (this.currentFormDef.byRequestOnly) {
            this.displayNewFormRequestDialog = true;
            return;
        }

        if (this.currentFormDef.activeRevision?.mufCapable) {
            this.currentMUFDef = await this.multiUserFormService.getByFormRevisionId(this.currentFormDef.activeRevisionId);
            this.displayMufCreateInstanceDialog = true;
            return;
        }

        this.navigateToFormDisplay();
    }

    async onSkipOkEpisodeSelectDialog(episode: IEpisode = null) {
        this.currentEpisode = episode;
        this.displaySelectEpisodeDialog = false;
        this.restoreDuplicateCheakState = this.restoreSelectControlState = false;

        if (this.newMufAssocData) {
            this.newMufAssocData.episodeId = this.currentEpisode.id;
            this.newMufAssocData.isReadOnlyEpisode = true;
            this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
            this.navigateToFormDisplay();
        }
    }

    onCancelEpisodeSelectDialog() {
        this.newMufAssocData = this.mufAssoc = null;
        this.deleteDtaftEpisode();
        this.currentEpisode = null;
        this.displaySelectEpisodeDialog = false;
        this.restoreDuplicateCheakState = this.restoreSelectControlState = false;
    }

    async deleteDtaftEpisode() {
        if (!this.currentEpisode || this.currentEpisode?.state != EpisodeStatus.FormDraft) {
            return;
        }
        await this.episodeService.deleteEpisode(this.currentEpisode.id, <IHttpResponseHandlerSettings>{
            showSuccessMessage: false
        });
        this.displaySelectEpisodeDialog = false;
    }

    onCancelCreateInstanceDialog() {
        this.deleteDtaftEpisode();
        this.currentMUFDef = null;
        this.displayMufCreateInstanceDialog = false;
    }

    async onCreateInstanceDialog(mufAssocData: NewMultiUserFormAssociation) {
        this.newMufAssocData = mufAssocData;

        if (!this.restoreSelectControlState && !this.restoreDuplicateCheakState) {
            this.savingDataLocally()
        }

        this.displaySelectFormDialog = false;
        this.displayMufCreateInstanceDialog = false;

        const selfTargetStage = this.newMufAssocData.multiUserFormAssociationLinks.some(x => x.userId == this.userService.currentUser.id);
        if (!this.currentEpisode && this.episodeRequired && !selfTargetStage) {
            this.displaySelectEpisodeDialog = true;
            return;
        }

        this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
        this.navigateToFormDisplay();
    }

    onCancelFormRequestDialog() {
        this.displayNewFormRequestDialog = false;
    }

    onCreateFormRequestDialog() {
        this.displayNewFormRequestDialog = false;
        this.displaySelectEpisodeDialog = false;
        this.displaySelectFormDialog = false;
    }

    async restoreState() {
        if (this.restoreDuplicateCheakState) {
            const path = removePathParam(this.location.path(), "restoreDuplicateCheakState");
            this.location.replaceState(path);
        }

        if (this.restoreSelectControlState) {
            const path = removePathParam(this.location.path(), "restoreSelectControlState");
            this.location.replaceState(path);
        }

        this.currentEpisode = AppStorage.getItemFromSession("episode");

        const formDefId: string = AppStorage.getItemFromSession("formDataId");
        if (formDefId) {
            this.currentFormDef = await this.formIODefinitionService.get(formDefId);
        }

        this.episodeRequired = AppStorage.getItemFromSession("episodeRequired");
        this.newMufAssocData = AppStorage.getItemFromSession("newMufAssocData");

        this.displaySelectEpisodeDialog = true;

        if (this.currentFormDef.activeRevision.mufCapable) {
            this.currentMUFDef = await this.multiUserFormService.getByFormRevisionId(this.currentFormDef.activeRevisionId);
        }
    }

    savingDataLocally() {
        AppStorage.setItemForSession("newMufAssocData", this.newMufAssocData);
        AppStorage.setItemForSession("formDataId", this.currentFormDef.id);
        AppStorage.setItemForSession("episode", this.currentEpisode);
        AppStorage.setItemForSession("episodeRequired", this.episodeRequired);
    }

    private async navigateToFormDisplay() {
        let queryParams = {};

        if (this.mufAssoc) {
            this.redirectToMUFInstance(queryParams);
            this.mufAssoc = null;
            return;
        }

        const latestRevisionForCurrentForm = await this.formioViewerService.get(this.currentFormDef.id);
        queryParams["formId"] = this.currentFormDef.id;
        queryParams["revisionId"] = latestRevisionForCurrentForm.activeRevision.id;

        if (this.currentEpisode) {
            queryParams["episodeId"] = this.currentEpisode.id;
        }

        queryParams["patientId"] = this.patientService.currentPatientId;
        queryParams["returnUrl"] = this.returnUrl ?? this.currentUrl;

        const componentTypes: CustomComponentTypes = await this.formIODefinitionService.getCustomComponentTypes(this.currentFormDef.id)
        if (componentTypes &
            (CustomComponentTypes.ConsultationHistoryV2
                | CustomComponentTypes.ConsultationHistory
                | CustomComponentTypes.EpisodeNotes
                | CustomComponentTypes.MedicationHistory)) {
            await this.redirectToDataInsertion(queryParams);
            return;
        }

        this.router.navigate(["app/formio/display"], { queryParams }).then();
    }

    redirectToMUFInstance(queryParams: { [key: string]: string | number }) {
        let startMufSelf = this.mufAssoc.multiUserFormAssociationLinks.find(x => x.multiUserFormStage.sequenceStepIndex == 1)?.userId == this.userService.currentUser.id;
        this.currentMUFDef = null;

        if (startMufSelf) {
            queryParams["mufId"] = this.mufAssoc.id;
            queryParams["target"] = this.userService.currentUser.id;
            queryParams["formDataId"] = this.mufAssoc.formDataId;
            queryParams["returnUrl"] = this.returnUrl ?? this.currentUrl;

            this.router.navigate(["app/formio/multiuser/display"], { queryParams }).then();
        }
    }


    async redirectToDataInsertion(queryParams: { [key: string]: string | number; }) {
        await this.router.navigate(["app/formio/data-insertion"], {queryParams});
    }
}